import { For, JSX, Match, Show, Switch } from 'solid-js';
import { SetStoreFunction } from 'solid-js/store';
import { PaneLayouts, PaneManagerConfig } from '../models/pane-manager-models';

export interface LayoutSelectorProps<T> {
  layouts: PaneLayouts<T>;
  setLayouts: SetStoreFunction<PaneLayouts<T>>;
  /** Maps a content config to an icon name string for rendering in the layout preview. */
  iconResolver(config: T): string;
  /** Optional title text displayed above the layout options. */
  title?: string;
  /** Optional custom icon renderer. If not provided, renders a basic icon element. */
  renderIcon?(iconName: string): JSX.Element;
  /** Optional resolver that maps a content config to a `data-testid` string for each cell. */
  testIdResolver?(config: T): string;
}

/**
 * Generic layout selector component that renders a grid of selectable layout presets.
 * Each preset shows a visual preview of its pane arrangement with icons.
 */
export const LayoutSelector = <T,>(
  props: LayoutSelectorProps<T>
): JSX.Element => {
  const LayoutView = (viewProps: {
    layout: PaneManagerConfig<T>;
  }): JSX.Element => {
    return (
      <div class="flex flex-col w-full h-full overflow-hidden rounded-sm border border-gray-0">
        <For each={viewProps.layout.paneRows}>
          {(row) => (
            <div class="flex flex-1 border-b last:border-b-0 border-gray-0">
              <For each={row.cells}>
                {(cell) => (
                  <div
                    class="flex-1 flex items-center justify-center border-r last:border-r-0 border-gray-0"
                    data-testid={props.testIdResolver?.(cell.contentConfig)}
                  >
                    <Switch>
                      <Match when={props.renderIcon}>
                        {// eslint-disable-next-line custom/no-direct-solid-component-invocation -- tracked by TWENG-38
                        props.renderIcon?.(
                          props.iconResolver(cell.contentConfig)
                        )}
                      </Match>
                      <Match when={!props.renderIcon}>
                        <span class={props.iconResolver(cell.contentConfig)} />
                      </Match>
                    </Switch>
                  </div>
                )}
              </For>
            </div>
          )}
        </For>
      </div>
    );
  };

  return (
    <div>
      <Show when={props.title}>
        <div class="px-2 py-1 font-semibold">{props.title}</div>
      </Show>
      <div class="mx-2 mt-2 grid grid-cols-[repeat(2,minmax(120px,1fr))] gap-4">
        <For each={props.layouts.layouts}>
          {(layout) => {
            const isActive = (): boolean =>
              props.layouts.activeLayoutLabel === layout.label;

            return (
              <Switch>
                <Match when={isActive()}>
                  <div
                    data-testid="layout-option"
                    aria-selected={true}
                    class="border rounded-md overflow-hidden
                      shadow-[0_0_0.2rem_rgba(0,0,0,0.2)]
                      hover:shadow-[0_0_0.375rem_rgba(0,0,0,0.2)]
                      border-blue"
                    onClick={() => {
                      props.setLayouts('activeLayoutLabel', layout.label);
                    }}
                  >
                    <div class="aspect-[16/9] opacity-60">
                      <LayoutView layout={layout} />
                    </div>
                    <div class="p-2 bg-blue text-white">{layout.label}</div>
                  </div>
                </Match>
                <Match when={!isActive()}>
                  <div
                    data-testid="layout-option"
                    aria-selected={false}
                    class="border rounded-md overflow-hidden
                      shadow-[0_0_0.2rem_rgba(0,0,0,0.2)]
                      hover:shadow-[0_0_0.375rem_rgba(0,0,0,0.2)]
                      border-gray-0 cursor-pointer"
                    onClick={() => {
                      props.setLayouts('activeLayoutLabel', layout.label);
                    }}
                  >
                    <div class="aspect-[16/9] opacity-60">
                      <LayoutView layout={layout} />
                    </div>
                    <div class="p-2 bg-gray-light">{layout.label}</div>
                  </div>
                </Match>
              </Switch>
            );
          }}
        </For>
      </div>
    </div>
  );
};
